import {
  AdditiveBlending,
  BoxGeometry,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  RingGeometry,
  TorusGeometry,
  Vector3
} from 'three';
import { planSpellHit, STATUS } from './combatRules.js';

const ARENA_RADIUS = 12;
const PLAYER_SPEED = 5.8;
const TARGET_RADIUS = 0.75;

const _forward = new Vector3();
const _right = new Vector3();
const _move = new Vector3();

const TARGET_LAYOUT = Object.freeze([
  { x: 0, z: -7.5 },
  { x: -5.4, z: -3.5 },
  { x: 5.4, z: -3.5 },
  { x: -5.8, z: 4.2 },
  { x: 5.8, z: 4.2, patrol: true }
]);

function createArena() {
  const group = new Group();
  group.name = 'Combat test arena';

  const ringMaterial = new MeshBasicMaterial({
    color: 0x4fc6e8,
    transparent: true,
    opacity: 0.18,
    depthWrite: false,
    blending: AdditiveBlending
  });

  for (const radius of [4, 8, ARENA_RADIUS]) {
    const ring = new Mesh(new RingGeometry(radius - 0.025, radius + 0.025, 128), ringMaterial);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.025;
    group.add(ring);
  }

  const boundaryMaterial = ringMaterial.clone();
  boundaryMaterial.opacity = 0.45;
  const boundary = new Mesh(new TorusGeometry(ARENA_RADIUS, 0.045, 8, 160), boundaryMaterial);
  boundary.rotation.x = Math.PI / 2;
  boundary.position.y = 0.05;
  group.add(boundary);

  return group;
}

function createTarget(index, layout) {
  const root = new Group();
  root.name = `Target dummy ${index + 1}`;
  root.position.set(layout.x, 0, layout.z);

  const bodyMaterial = new MeshStandardMaterial({
    color: 0x394b60,
    roughness: 0.48,
    metalness: 0.58,
    emissive: 0x10273a,
    emissiveIntensity: 0.82
  });
  const body = new Mesh(new CylinderGeometry(0.58, 0.74, 1.55, 12), bodyMaterial);
  body.position.y = 0.82;
  body.castShadow = true;
  body.receiveShadow = true;
  root.add(body);

  const coreMaterial = new MeshStandardMaterial({
    color: 0xe8f7ff,
    roughness: 0.16,
    metalness: 0.2,
    emissive: 0x4fc6e8,
    emissiveIntensity: 2.4
  });
  const core = new Mesh(new IcosahedronGeometry(0.34, 1), coreMaterial);
  core.position.y = 1.25;
  root.add(core);

  const collar = new Mesh(
    new TorusGeometry(0.5, 0.055, 8, 28),
    new MeshStandardMaterial({ color: 0x7d91a8, roughness: 0.32, metalness: 0.8 })
  );
  collar.rotation.x = Math.PI / 2;
  collar.position.y = 1.42;
  root.add(collar);

  const statusMaterial = new MeshBasicMaterial({
    color: 0x6edcff,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    blending: AdditiveBlending
  });
  const statusRing = new Mesh(new TorusGeometry(0.88, 0.05, 8, 40), statusMaterial);
  statusRing.rotation.x = Math.PI / 2;
  statusRing.position.y = 0.08;
  root.add(statusRing);

  const health = new Group();
  health.position.y = 2.05;
  const healthBack = new Mesh(
    new BoxGeometry(1.45, 0.13, 0.03),
    new MeshBasicMaterial({ color: 0x10151b, transparent: true, opacity: 0.9, depthTest: false })
  );
  const healthFront = new Mesh(
    new BoxGeometry(1.3, 0.07, 0.04),
    new MeshBasicMaterial({ color: 0x76e5a0, depthTest: false })
  );
  healthFront.position.z = 0.02;
  health.add(healthBack, healthFront);
  root.add(health);

  return {
    id: index,
    root,
    bodyMaterial,
    core,
    coreMaterial,
    statusRing,
    statusMaterial,
    health,
    healthFront,
    maxHealth: 120,
    healthValue: 120,
    chilled: 0,
    snared: 0,
    hitFlash: 0,
    deadTimer: 0,
    layout,
    phase: index * 1.37
  };
}

/** A deliberately small gameplay layer around the procedural spell renderer. */
export class CombatSandbox {
  constructor({ scene, camera, onEvent }) {
    this.scene = scene;
    this.camera = camera;
    this.onEvent = onEvent;
    this.elapsed = 0;
    this.pending = [];
    this._impactSequence = 0;
    this.stats = { casts: 0, hits: 0, defeated: 0, combos: new Set() };

    this.group = createArena();
    this.targets = TARGET_LAYOUT.map((layout, index) => createTarget(index, layout));
    for (const target of this.targets) this.group.add(target.root);
    scene.add(this.group);
  }

  movePlayer(keys, dt, character) {
    if (dt <= 0) return;

    this.camera.getWorldDirection(_forward);
    _forward.y = 0;
    if (_forward.lengthSq() < 1e-6) _forward.set(0, 0, -1);
    _forward.normalize();
    _right.crossVectors(_forward, this.camera.up).normalize();

    const x = (keys.has('KeyD') ? 1 : 0) - (keys.has('KeyA') ? 1 : 0);
    const z = (keys.has('KeyW') ? 1 : 0) - (keys.has('KeyS') ? 1 : 0);
    _move.set(0, 0, 0).addScaledVector(_right, x).addScaledVector(_forward, z);
    if (_move.lengthSq() < 1e-6) return;

    _move.normalize();
    character.root.position.addScaledVector(_move, PLAYER_SPEED * dt);
    const flatLength = Math.hypot(character.position.x, character.position.z);
    const limit = ARENA_RADIUS - 1;
    if (flatLength > limit) {
      character.root.position.x *= limit / flatLength;
      character.root.position.z *= limit / flatLength;
    }

    character.turnToward(Math.atan2(_move.x, _move.z), 0.003, dt);
  }

  registerCast({ element, castShape, origin, direction, distance, speed, zoneRadius }) {
    this.stats.casts++;
    let contacts = 0;

    for (const target of this.targets) {
      if (target.deadTimer > 0) continue;
      const plan = planSpellHit({
        element,
        castShape,
        origin,
        direction,
        distance,
        speed,
        zoneRadius,
        target: target.root.position,
        targetRadius: TARGET_RADIUS
      });
      if (!plan) continue;
      contacts++;
      this.pending.push({ ...plan, target, sequence: this._impactSequence++ });
    }

    if (contacts === 0) {
      this.pending.push({
        delay: Math.max(0.08, distance / Math.max(1, speed ?? 18)),
        miss: true,
        element,
        sequence: this._impactSequence++
      });
    }

    this._emit('cast', { element, contacts });
  }

  update(dt) {
    this.elapsed += dt;

    const due = [];
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const impact = this.pending[i];
      impact.delay -= dt;
      if (impact.delay > 0) continue;
      this.pending.splice(i, 1);
      due.push(impact);
    }

    // A throttled/background frame can make several contacts due together.
    // Preserve the order they were cast so setup spells always resolve before
    // their detonators, independent of array-removal direction.
    due.sort((a, b) => a.sequence - b.sequence);
    for (const impact of due) {
      if (impact.miss) this._emit('miss', { element: impact.element });
      else this._applyImpact(impact);
    }

    for (const target of this.targets) this._updateTarget(target, dt);
  }

  _applyImpact(impact) {
    const target = impact.target;
    if (target.deadTimer > 0) return;

    let damage = impact.damage;
    let combo = null;
    if (impact.consumes && target[impact.consumes] > 0) {
      target[impact.consumes] = 0;
      damage += impact.comboDamage;
      combo = impact.combo;
      this.stats.combos.add(combo);
    }

    if (impact.applies) target[impact.applies] = Math.max(target[impact.applies], impact.statusDuration);
    target.healthValue = Math.max(0, target.healthValue - damage);
    target.hitFlash = 1;
    this.stats.hits++;

    this._emit('hit', {
      element: impact.element,
      target: target.id,
      damage,
      combo,
      health: target.healthValue
    });

    if (target.healthValue <= 0) {
      target.deadTimer = 2.4;
      target.root.visible = false;
      this.stats.defeated++;
      this._emit('defeated', { target: target.id, combo });
    }
  }

  _updateTarget(target, dt) {
    if (target.deadTimer > 0) {
      target.deadTimer -= dt;
      if (target.deadTimer <= 0) this._respawn(target);
      return;
    }

    target.chilled = Math.max(0, target.chilled - dt);
    target.snared = Math.max(0, target.snared - dt);
    target.hitFlash = Math.max(0, target.hitFlash - dt * 4.2);

    if (target.layout.patrol && target.snared <= 0) {
      const radius = 1.8;
      const speed = target.chilled > 0 ? 0.28 : 0.62;
      const angle = this.elapsed * speed + target.phase;
      target.root.position.x = target.layout.x + Math.cos(angle) * radius;
      target.root.position.z = target.layout.z + Math.sin(angle) * radius;
    }

    target.bodyMaterial.emissive.set(target.hitFlash > 0 ? 0xff734d : 0x10273a);
    target.bodyMaterial.emissiveIntensity = 0.82 + target.hitFlash * 4;
    target.core.scale.setScalar(1 + target.hitFlash * 0.34);
    target.core.rotation.y += dt * (target.snared > 0 ? 8 : 1.8);

    const ratio = target.healthValue / target.maxHealth;
    target.healthFront.scale.x = ratio;
    target.healthFront.position.x = -(1 - ratio) * 0.65;
    target.healthFront.material.color.set(ratio > 0.5 ? 0x76e5a0 : ratio > 0.25 ? 0xffc85a : 0xff655f);
    target.health.quaternion.copy(this.camera.quaternion);

    const hasStatus = target.chilled > 0 || target.snared > 0;
    target.statusMaterial.opacity = hasStatus ? 0.75 : 0;
    if (target.chilled > 0 && target.snared > 0) target.statusMaterial.color.set(0xe7f4ff);
    else if (target.chilled > 0) target.statusMaterial.color.set(0x6edcff);
    else target.statusMaterial.color.set(0xa98bff);
    target.statusRing.rotation.z += dt * (target.snared > 0 ? 3.8 : 1.5);
  }

  _respawn(target) {
    target.deadTimer = 0;
    target.healthValue = target.maxHealth;
    target.chilled = 0;
    target.snared = 0;
    target.hitFlash = 0;
    target.root.visible = true;
    target.root.position.set(target.layout.x, 0, target.layout.z);
    this._emit('respawn', { target: target.id });
  }

  reset() {
    this.pending.length = 0;
    this.elapsed = 0;
    this._impactSequence = 0;
    this.stats.casts = 0;
    this.stats.hits = 0;
    this.stats.defeated = 0;
    this.stats.combos.clear();
    for (const target of this.targets) this._respawn(target);
    this._emit('reset', {});
  }

  snapshot() {
    return {
      casts: this.stats.casts,
      hits: this.stats.hits,
      defeated: this.stats.defeated,
      combos: [...this.stats.combos],
      targets: this.targets.map((target) => ({
        id: target.id,
        health: target.healthValue,
        chilled: target.chilled > 0,
        snared: target.snared > 0,
        active: target.deadTimer <= 0
      }))
    };
  }

  _emit(type, detail) {
    this.onEvent?.({ type, ...detail, state: this.snapshot() });
  }

  dispose() {
    this.scene.remove(this.group);
    this.group.traverse((object) => {
      object.geometry?.dispose?.();
      if (Array.isArray(object.material)) object.material.forEach((material) => material.dispose());
      else object.material?.dispose?.();
    });
    this.pending.length = 0;
  }
}
